import { usePermissions } from '@/shared/hooks/usePermissions';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import {
  CAlert,
  CButton,
  CFormSelect,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import React, { useState } from 'react';
import {
  getContractShippingCode,
  getContractShippingDisplayTitle,
  SHIPPING_OPTION_VALUES,
  useSubscriptionContract,
  type ShippingOptionValue,
} from '../../hooks/useSubscriptionContract';
import { formatDate, getPhoneNumber } from './utils';

interface SubscriptionContractProps {
  shopifyCustomerId: string;
}

export const SubscriptionContract: React.FC<SubscriptionContractProps> = ({
  shopifyCustomerId,
}) => {
  const {
    contract,
    loading,
    error,
    updateShippingOption,
    shippingUpdateLoading,
    shippingUpdateError,
    clearShippingUpdateError,
  } = useSubscriptionContract(shopifyCustomerId);
  const { hasPermission } = usePermissions();
  const canUpdateShipping = hasPermission(
    USER_PERMISSIONS.CustomersSubscriptionContractUpdateShipping,
  );
  const [selectedShippingOption, setSelectedShippingOption] = useState<ShippingOptionValue>('DPD');
  const [previousContract, setPreviousContract] = useState(contract);

  if (contract !== previousContract) {
    setPreviousContract(contract);
    setSelectedShippingOption(contract ? getContractShippingCode(contract) : 'DPD');
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <CSpinner />
      </div>
    );
  }

  if (error) {
    return <CAlert color="danger">{error}</CAlert>;
  }

  if (!contract) {
    return <CAlert color="info">No subscription contract found for this customer.</CAlert>;
  }

  const subtotal = contract.lines.reduce((sum, line) => {
    return sum + parseFloat(line.currentPrice.amount) * line.quantity;
  }, 0);

  const total = contract.lines.reduce((sum, line) => {
    return sum + parseFloat(line.lineDiscountedPrice.amount);
  }, 0);

  const phoneNumber = getPhoneNumber(contract);

  return (
    <div>
      <div className="mb-4">
        <h5>Subscription Contract Status</h5>
        <dl className="row mb-3">
          <dt className="col-sm-3">Status:</dt>
          <dd className="col-sm-9">{contract.status}</dd>
          <dt className="col-sm-3">Next Billing Date:</dt>
          <dd className="col-sm-9">{formatDate(contract.nextBillingDate)}</dd>
          <dt className="col-sm-3">Last Payment Status:</dt>
          <dd className="col-sm-9">{contract.lastPaymentStatus || 'N/A'}</dd>
        </dl>
      </div>

      <div className="mb-4">
        <h5>Products</h5>
        <CTable striped hover>
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell>Product</CTableHeaderCell>
              <CTableHeaderCell>SKU</CTableHeaderCell>
              <CTableHeaderCell>Qty</CTableHeaderCell>
              <CTableHeaderCell>Price</CTableHeaderCell>
              <CTableHeaderCell>Subtotal</CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {contract.lines.map((line) => (
              <CTableRow key={line.id}>
                <CTableDataCell>{line.title}</CTableDataCell>
                <CTableDataCell>{line.sku}</CTableDataCell>
                <CTableDataCell>{line.quantity}</CTableDataCell>
                <CTableDataCell>£{parseFloat(line.currentPrice.amount).toFixed(2)}</CTableDataCell>
                <CTableDataCell>
                  £{parseFloat(line.lineDiscountedPrice.amount).toFixed(2)}
                </CTableDataCell>
              </CTableRow>
            ))}
            {contract.discounts.map((discount) => (
              <CTableRow key={discount.id}>
                <CTableDataCell colSpan={4} className="text-end">
                  <strong>{discount.title}</strong>
                </CTableDataCell>
                <CTableDataCell>
                  {discount.value.percentage !== undefined
                    ? `${discount.value.percentage}%`
                    : discount.value.amount
                      ? `£${parseFloat(discount.value.amount.amount).toFixed(2)}`
                      : 'N/A'}
                </CTableDataCell>
              </CTableRow>
            ))}
            <CTableRow>
              <CTableDataCell colSpan={4} className="text-end">
                <strong>Subtotal</strong>
              </CTableDataCell>
              <CTableDataCell>
                <strong>£{subtotal.toFixed(2)}</strong>
              </CTableDataCell>
            </CTableRow>
            <CTableRow>
              <CTableDataCell colSpan={4} className="text-end">
                <strong>Total</strong>
              </CTableDataCell>
              <CTableDataCell>
                <strong>£{total.toFixed(2)}</strong>
              </CTableDataCell>
            </CTableRow>
          </CTableBody>
        </CTable>
      </div>

      <div className="mb-4">
        <h5>Customer Details</h5>
        <dl className="row">
          <dt className="col-sm-3">Firstname:</dt>
          <dd className="col-sm-9">{contract.customer?.firstName || 'N/A'}</dd>
          <dt className="col-sm-3">Lastname:</dt>
          <dd className="col-sm-9">{contract.customer?.lastName || 'N/A'}</dd>
          <dt className="col-sm-3">E-mail:</dt>
          <dd className="col-sm-9">{contract.customer?.email || 'N/A'}</dd>
        </dl>
      </div>

      <div className="mb-4">
        <h5>Payment Details</h5>
        {contract.customerPaymentMethod?.id && !contract.customerPaymentMethod?.revokedAt ? (
          <dl className="row">
            <dt className="col-sm-3">Brand:</dt>
            <dd className="col-sm-9">
              {contract.customerPaymentMethod.instrument?.brand || 'N/A'}
            </dd>
            <dt className="col-sm-3">Last Digits:</dt>
            <dd className="col-sm-9">
              {contract.customerPaymentMethod.instrument?.lastDigits || 'N/A'}
            </dd>
          </dl>
        ) : (
          <dl className="row">
            <dt className="col-sm-3">Payment method revoked At:</dt>
            <dd className="col-sm-9">
              {contract.customerPaymentMethod?.revokedAt
                ? formatDate(contract.customerPaymentMethod.revokedAt)
                : 'N/A'}
            </dd>
          </dl>
        )}
      </div>

      <div className="mb-4">
        <h5>Contract Delivery Address Details</h5>
        <dl className="row">
          <dt className="col-sm-3">Firstname:</dt>
          <dd className="col-sm-9">{contract.deliveryMethod?.address?.firstName || 'N/A'}</dd>
          <dt className="col-sm-3">Lastname:</dt>
          <dd className="col-sm-9">{contract.deliveryMethod?.address?.lastName || 'N/A'}</dd>
          <dt className="col-sm-3">Phone:</dt>
          <dd className="col-sm-9">{phoneNumber}</dd>
          <dt className="col-sm-3">Line 1:</dt>
          <dd className="col-sm-9">{contract.deliveryMethod?.address?.address1 || 'N/A'}</dd>
          <dt className="col-sm-3">Line 2:</dt>
          <dd className="col-sm-9">
            {contract.deliveryMethod?.address?.address2 || 'Not provided'}
          </dd>
          <dt className="col-sm-3">City:</dt>
          <dd className="col-sm-9">{contract.deliveryMethod?.address?.city || 'N/A'}</dd>
          <dt className="col-sm-3">Country:</dt>
          <dd className="col-sm-9">{contract.deliveryMethod?.address?.countryCode || 'N/A'}</dd>
          <dt className="col-sm-3">Postcode:</dt>
          <dd className="col-sm-9">{contract.deliveryMethod?.address?.zip || 'N/A'}</dd>
        </dl>
      </div>
      <div className="mb-4">
        <h5>Contract Shipping Option</h5>
        {shippingUpdateError ? (
          <CAlert color="danger" className="mb-2">
            {shippingUpdateError}
          </CAlert>
        ) : null}
        <dl className="row">
          <dt className="col-sm-3">Code:</dt>
          <dd className="col-sm-9">
            {canUpdateShipping ? (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <CFormSelect
                  id="contract-shipping-option-code"
                  className="w-auto"
                  style={{ minWidth: '12rem' }}
                  value={selectedShippingOption}
                  aria-label="Subscription contract shipping carrier"
                  onChange={(e) => {
                    clearShippingUpdateError();
                    setSelectedShippingOption(e.target.value as ShippingOptionValue);
                  }}
                  disabled={shippingUpdateLoading}
                >
                  {SHIPPING_OPTION_VALUES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </CFormSelect>
                <CButton
                  color="primary"
                  size="sm"
                  disabled={shippingUpdateLoading}
                  onClick={() => void updateShippingOption(selectedShippingOption)}
                >
                  {shippingUpdateLoading ? 'Updating…' : 'Update'}
                </CButton>
              </div>
            ) : (
              <span>{getContractShippingCode(contract)}</span>
            )}
          </dd>
          <dt className="col-sm-3">Title:</dt>
          <dd className="col-sm-9">{getContractShippingDisplayTitle(contract)}</dd>
        </dl>
      </div>
    </div>
  );
};
